import { useApi } from '@src/app/context/Api';
import { User } from '@src/entities/User/ui/components/User';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import React from 'react';

import { Styles } from './includes/styles';

/**
 * Modal: RemoveEid
 *
 * Displays a modal popup to remove EID's from the list of associated EID's with the action path.
 *
 * @param actionPath
 * @param eids
 * @param isOpen
 * @param refetch
 * @param setEids
 * @param setIsOpen
 * @returns {Element}
 * @constructor
 */
export function RemoveEid({
  actionPath = {},
  eids = [],
  isOpen = false,
  refetch = () => {},
  setEids = () => {},
  setIsOpen = () => {},
}) {
  const { toast } = useToast();

  const { clearApiCache, useApiMutation } = useApi('RiskRecord');
  const [RiskRecordActionPathDisassociation] = useApiMutation('RiskRecordActionPathDisassociation');

  /**
   * handleClose
   *
   * Closes the modal popup and resets the EID.
   */
  const handleClose = () => {
    setEids([]);
    setIsOpen(false);
  };

  /**
   * handleDisassociation
   *
   * Sends an api request to disassociate the EID's from the action path.
   *
   * @returns {Promise<void>}
   */
  const handleDisassociation = async () => {
    const toastId = 'eid-disassociation';
    toast.show({
      ariaLoadingLabel: "Disassociating EID's From Action Path",
      autoClose: false,
      id: `${toastId}-info`,
      isLoading: true,
      message: "Action Path is preparing to disassociate the EID's.",
      title: "Disassociating EID's From Action Path...",
      variant: 'info',
    });

    handleClose();

    const payload = {
      actionPaths: [actionPath?.id],
      eids,
    };

    await RiskRecordActionPathDisassociation(payload, {
      onError: () => {
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-error`,
          message: "Unable to disassociate EID's.",
          title: "Disassociating EID's Failed",
          variant: 'error',
        });
      },
      onSuccess: async () => {
        await clearApiCache([{ apiKey: 'RiskRecord', queryKeys: ['ListRiskRecords'] }]);
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-success`,
          message: "EID's have been disassociated.",
          title: "Disassociated EID's From Action Path",
          variant: 'success',
        });

        setTimeout(() => {
          refetch();
        }, 750);
      },
    });
  };

  return (
    <ErrorHandler location="src/features/ActionPaths/RemoveEid/RemoveEid.jsx">
      <User.Capability attributes={['detach']} method="hide" resource="ActionPaths">
        <Modal
          footer={
            <Modal.Section>
              <Divider height={1} />
              <Layout.Group alignLayout="right">
                <Button onClick={handleClose} variant="outline">
                  Cancel
                </Button>
                <Button onClick={handleDisassociation} variant="destructive">
                  Remove Association
                </Button>
              </Layout.Group>
            </Modal.Section>
          }
          isOpen={isOpen}
          onClose={handleClose}
          title="Confirm Association Removal"
        >
          <Styles>
            <Modal.Section>
              {eids?.length > 1 ? (
                <React.Fragment>
                  Are you sure you want to remove the following associated EID's: <strong>{eids?.join(', ')}</strong> ?
                </React.Fragment>
              ) : (
                <React.Fragment>
                  Are you sure you want to remove associated EID: <strong>{eids?.[0]}</strong> ?
                </React.Fragment>
              )}
            </Modal.Section>
          </Styles>
        </Modal>
      </User.Capability>
    </ErrorHandler>
  );
}
